// Task 24575 — ask for the template placeholders as the template loads, then
// replace them on the fly. Opened automatically by SendEmail when a template
// still contains {{tokens}} the backend could not resolve, and reopenable from
// the "Fill placeholders" button next to the subject.

import React, { useEffect, useMemo, useState } from "react";
import { Input, Label } from "reactstrap";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import AppModal from "../AppModal";
import { humanisePlaceholder } from "../emailPlaceholders";

const EmailPlaceholderFill = ({
  open,
  onClose,
  placeholders = [],
  initialValues = {},
  onApply,
}) => {
  const [values, setValues] = useState({});

  // Re-seed each time the modal opens so values typed for a previous template
  // don't leak into the next one, while keeping anything already answered.
  useEffect(() => {
    if (!open) return;
    setValues(
      placeholders.reduce((out, name) => {
        out[name] = initialValues?.[name] ?? "";
        return out;
      }, {})
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, placeholders]);

  const allFilled = useMemo(
    () => placeholders.every((name) => (values?.[name] ?? "").trim() !== ""),
    [placeholders, values]
  );

  const handleApply = () => {
    // Trim on the way out — a stray space inside an email body is invisible
    // and impossible to spot afterwards.
    const trimmed = Object.keys(values).reduce((out, key) => {
      out[key] = (values[key] ?? "").trim();
      return out;
    }, {});
    onApply?.(trimmed);
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title="Fill in the template placeholders"
      subtitle="These values are dropped into the subject and message before you send."
      icon={<EditNoteOutlinedIcon />}
      headerAccent="indigo"
      size="sm"
      onSubmit={handleApply}
      submitLabel="Apply"
      submitDisabled={placeholders.length === 0}
      cancelLabel="Skip for now"
    >
      {placeholders.length === 0 ? (
        <div className="text-muted">
          There are no placeholders left to fill in.
        </div>
      ) : (
        <>
          {placeholders.map((name) => (
            <div className="mb-3" key={name}>
              <Label className="form-label" for={`placeholder-${name}`}>
                {humanisePlaceholder(name)}
              </Label>
              <Input
                id={`placeholder-${name}`}
                type="text"
                className="form-control"
                placeholder={`{{${name}}}`}
                value={values?.[name] ?? ""}
                onChange={(e) =>
                  setValues((prev) => ({ ...prev, [name]: e.target.value }))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" && allFilled) handleApply();
                }}
              />
            </div>
          ))}
          {!allFilled && (
            <div className="text-muted small">
              Anything you leave blank stays as its {`{{placeholder}}`} so you can
              come back to it — but the email can't be sent until they're all
              replaced.
            </div>
          )}
        </>
      )}
    </AppModal>
  );
};

export default EmailPlaceholderFill;
